import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { clearActiveOrg, subscribeActiveOrgRejected } from "@piiqtechnologies/ui-lib";
import { PATHS } from "@constants/Paths";
import { clearStoredMemberships } from "@stores/memberships";
/**
 * Recovers from an active organization the backend no longer accepts.
 *
 * `ActiveOrgLayout` authorizes `/o/:orgId` against the per-tab membership mirror, so the app never
 * sends a header for an organization it believes the caller doesn't hold. What it cannot know is
 * that the belief expired: the backend resolves memberships from peek-auth policies on every
 * request, so access revoked elsewhere mid-session leaves the mirror listing an organization the
 * caller has lost. Every subsequent request then fails on a header the app keeps re-sending, with
 * nothing in the UI able to correct it — the resolver re-authorizes from the same stale mirror.
 *
 * Dropping both halves of the mirror is what breaks that: with no stored memberships the next
 * reader re-fetches them (see `clearStoredMemberships`), and the organization picker resolves the
 * fresh answer — one organization, several, or none — instead of the expired one.
 */
export const useActiveOrgRecovery = (isStaff) => {
    const navigate = useNavigate();
    useEffect(() => {
        if (isStaff)
            return;
        return subscribeActiveOrgRejected(() => {
            clearStoredMemberships();
            clearActiveOrg();
            navigate(PATHS.SELECT_ORGANIZATION, { replace: true });
        });
    }, [isStaff, navigate]);
};
